import React, { useEffect, useState } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { createWorker, updateWorker, fetchWorkerById, generateInstallScript } from '../../../../redux/slices/workersSlice';
import Button from 'components/base/Button';
import { Modal, Form, Tabs, Tab, Table, Spinner } from 'react-bootstrap';
import FormatDateTime from 'components/common/FormatDateTime';
import { fetchWorkerLogFiles, downloadWorkerLogFile, getWorkerLogDownloadError, AgentLogFile } from 'helpers/workerLogDownloads';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';

export interface WorkerFormProps {
  worker_id?: number;
  worker_name: string;
  worker_secret?: string;
  pre_execution_script?: { type: string; script: string }[];
  enabled?: boolean;
  maintenance_mode_active?: boolean;
  node_instance_directory?: string;
  skip_ssl_validation?: boolean;
  accept_self_signed?: boolean;
  reconnect_interval?: number;
  sap_binary_directory?: string;
  sap_dyld_library_path?: string;
}

// The form always holds a secret: emptyForm seeds it, and the edit path blanks it. The
// record type above leaves it optional because it also types the stored rows passed in
// as `worker`, which do not carry one.
type WorkerFormState = WorkerFormProps & { worker_secret: string };

const normalizePreExecutionScriptType = (type: string) => (type === 'bash' ? 'shell' : type);

const PRE_EXECUTION_SCRIPT_TYPE_OPTIONS = [
  { value: 'shell', label: 'Shell' },
  { value: 'pwsh', label: 'PowerShell' },
  { value: 'cmd', label: 'Cmd' },
  { value: 'sqlplus', label: 'SQLPlus' },
];

const emptyForm = (): WorkerFormState => ({
  worker_id: undefined,
  worker_name: '',
  worker_secret: '',
  pre_execution_script: [],
  node_instance_directory: '',
  skip_ssl_validation: false,
  accept_self_signed: false,
  reconnect_interval: 5,
  sap_binary_directory: '',
  sap_dyld_library_path: '',
});

export interface EditWorkerModalProps {
  show: boolean;
  handleClose: () => void;
  mode: 'create' | 'edit';
  worker: WorkerFormProps | null;
}

/**
 * Create / edit a worker. Self-contained tabbed dialog: the Worker form,
 * Status History, Logging (agent log files) and Install Script generation. Owns
 * all its form/log/install-script state and persists via the worker thunks.
 */
const EditWorkerModal: React.FC<EditWorkerModalProps> = ({ show, handleClose, mode, worker }) => {
  const dispatch = useAppDispatch();

  const [formValues, setFormValues] = useState<WorkerFormState>(emptyForm());
  const [key, setKey] = useState('worker');
  const [agentLogFiles, setAgentLogFiles] = useState<AgentLogFile[]>([]);
  const [agentLogsInfo, setAgentLogsInfo] = useState<string | null>(null);
  const [isLoadingAgentLogs, setIsLoadingAgentLogs] = useState(false);
  const [agentLogsError, setAgentLogsError] = useState<string | null>(null);
  const [downloadingLogFileName, setDownloadingLogFileName] = useState<string | null>(null);
  const [installScriptServerUrl, setInstallScriptServerUrl] = useState('');
  const [installScriptSecret, setInstallScriptSecret] = useState('');
  const [installScriptDownloadUrl, setInstallScriptDownloadUrl] = useState('');
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  const [installScriptError, setInstallScriptError] = useState<string | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedWorker = useAppSelector((state) => state.workers.items.find((w) => w.worker_id === formValues.worker_id));

  const isDirty = useUnsavedChanges(show, formValues);

  // Initialise on open: prefill from the record for edit, or a blank form for
  // create; reset the log / install-script panels.
  useEffect(() => {
    if (!show) return;
    if (mode === 'edit' && worker) {
      setFormValues({
        ...worker,
        worker_secret: '',
        pre_execution_script: (worker.pre_execution_script || []).map((script) => ({ ...script, type: normalizePreExecutionScriptType(script.type) })),
        node_instance_directory: worker.node_instance_directory || '',
        skip_ssl_validation: worker.skip_ssl_validation ?? false,
        accept_self_signed: worker.accept_self_signed ?? false,
        reconnect_interval: worker.reconnect_interval ?? 5,
        sap_binary_directory: worker.sap_binary_directory || '',
        sap_dyld_library_path: worker.sap_dyld_library_path || '',
      });
    } else {
      setFormValues(emptyForm());
    }
    setKey('worker');
    setAgentLogFiles([]);
    setAgentLogsInfo(null);
    setAgentLogsError(null);
    setIsLoadingAgentLogs(false);
    setDownloadingLogFileName(null);
    setInstallScriptServerUrl('');
    setInstallScriptSecret('');
    setInstallScriptDownloadUrl('');
    setInstallScriptError(null);
    setIsGeneratingScript(false);
  }, [show, mode, worker]);

  const handleAddScript = () => {
    setFormValues({ ...formValues, pre_execution_script: [...(formValues.pre_execution_script || []), { type: '', script: '' }] });
  };

  const handleRemoveScript = (index: number) => {
    setFormValues({ ...formValues, pre_execution_script: (formValues.pre_execution_script || []).filter((_, i) => i !== index) });
  };

  const handleScriptChange = (index: number, field: keyof { type: string; script: string }, value: string) => {
    const updatedScripts = [...(formValues.pre_execution_script || [])];
    updatedScripts[index][field] = value;
    setFormValues({ ...formValues, pre_execution_script: updatedScripts });
  };

  const loadAgentLogs = async (workerId: number) => {
    setIsLoadingAgentLogs(true);
    setAgentLogsError(null);
    try {
      const response = await fetchWorkerLogFiles(workerId);
      setAgentLogFiles(response.files);
      setAgentLogsInfo(response.info || null);
    } catch (error: any) {
      setAgentLogFiles([]);
      setAgentLogsInfo(null);
      setAgentLogsError(error?.response?.data?.error || error?.message || 'Failed to load logs');
    } finally {
      setIsLoadingAgentLogs(false);
    }
  };

  const handleDownloadAgentLog = async (workerId: number, fileName: string) => {
    setDownloadingLogFileName(fileName);
    setAgentLogsError(null);
    try {
      await downloadWorkerLogFile(workerId, fileName);
    } catch (error) {
      setAgentLogsError(await getWorkerLogDownloadError(error));
    } finally {
      setDownloadingLogFileName(null);
    }
  };

  const formatFileSize = (sizeBytes: number) => {
    if (!Number.isFinite(sizeBytes) || sizeBytes < 0) return 'N/A';
    if (sizeBytes < 1024) return `${sizeBytes} B`;
    if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
    if (sizeBytes < 1024 * 1024 * 1024) return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(sizeBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const resetAndClose = () => {
    setFormValues(emptyForm());
    handleClose();
  };

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      resetAndClose();
    }
  };

  const handleSaveWorker = async () => {
    const {
      worker_id,
      worker_name,
      worker_secret,
      pre_execution_script,
      node_instance_directory,
      skip_ssl_validation,
      accept_self_signed,
      reconnect_interval,
      sap_binary_directory,
      sap_dyld_library_path,
    } = formValues;

    const installFields = {
      node_instance_directory: node_instance_directory || undefined,
      skip_ssl_validation,
      accept_self_signed,
      reconnect_interval,
      sap_binary_directory: sap_binary_directory || undefined,
      sap_dyld_library_path: sap_dyld_library_path || undefined,
    };

    setIsSubmitting(true);
    try {
      const result: any = worker_id
        ? await dispatch(updateWorker({ worker_id, worker_name, ...(worker_secret ? { worker_secret } : {}), pre_execution_script, ...installFields }) as any)
        : await dispatch(createWorker({ worker_name, worker_secret, pre_execution_script, ...installFields }) as any);
      if (result.meta.requestStatus === 'rejected') return;
      resetAndClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateInstallScript = async () => {
    if (!formValues.worker_id) return;
    if (!installScriptServerUrl || !installScriptSecret) {
      setInstallScriptError('Server URL and Worker Secret are required');
      return;
    }
    setIsGeneratingScript(true);
    setInstallScriptError(null);
    try {
      await dispatch(
        generateInstallScript({
          worker_id: formValues.worker_id,
          server_url: installScriptServerUrl,
          worker_secret: installScriptSecret,
          download_url: installScriptDownloadUrl || undefined,
        }) as any
      ).unwrap();
    } catch (err: any) {
      setInstallScriptError(err?.message || 'Failed to generate install script');
    } finally {
      setIsGeneratingScript(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleRequestClose}>
        <Modal.Header closeButton>
          <Modal.Title>{formValues.worker_id ? 'Edit Worker' : 'New Worker'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs
            id="worker-tabs"
            activeKey={key}
            onSelect={(k) => {
              setKey(k || 'worker');
              if (k === 'status' && formValues.worker_id) {
                dispatch(fetchWorkerById(formValues.worker_id) as any);
              }
              if (k === 'logging' && formValues.worker_id) {
                loadAgentLogs(formValues.worker_id);
              }
            }}
            className="mb-3"
          >
            <Tab eventKey="worker" title="Worker">
              <Form>
                <Form.Group controlId="formWorkerName">
                  <Form.Label>Worker Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formValues.worker_name || ''}
                    onChange={(e) => setFormValues({ ...formValues, worker_name: e.target.value.replace(/\s/g, '') })}
                    placeholder="Enter worker name"
                  />
                </Form.Group>

                <Form.Group controlId="formWorkerSecret">
                  <Form.Label>Worker Secret</Form.Label>
                  <Form.Control
                    type="password"
                    value={formValues.worker_secret || ''}
                    onChange={(e) => setFormValues({ ...formValues, worker_secret: e.target.value })}
                    placeholder="Enter new worker secret"
                  />
                  <Form.Text className="text-muted">Leave blank to keep the existing secret.</Form.Text>
                </Form.Group>

                <Form.Group controlId="formPreExecutionScripts">
                  <Form.Label>Pre-Execution Scripts</Form.Label>
                  {formValues.pre_execution_script?.map((script, index) => (
                    <div key={index} className="d-flex flex-column gap-2 mb-3 border p-3 rounded">
                      <Form.Group>
                        <Form.Label>Type</Form.Label>
                        <Form.Select value={script.type} onChange={(e) => handleScriptChange(index, 'type', e.target.value)}>
                          <option value="">Select Type</option>
                          {PRE_EXECUTION_SCRIPT_TYPE_OPTIONS.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group>
                        <Form.Label>Script</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={script.script}
                          onChange={(e) => handleScriptChange(index, 'script', e.target.value)}
                          placeholder="Enter script here"
                        />
                      </Form.Group>
                      <Button variant="danger" onClick={() => handleRemoveScript(index)}>
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button className="mx-4" variant="phoenix-primary" onClick={handleAddScript}>
                    Add Script
                  </Button>
                </Form.Group>

                <hr />
                <h6>Agent Configuration</h6>

                <Form.Group controlId="formNodeInstanceDirectory" className="mb-3">
                  <Form.Label>Node Instance Directory</Form.Label>
                  <Form.Control
                    type="text"
                    value={formValues.node_instance_directory || ''}
                    onChange={(e) => setFormValues({ ...formValues, node_instance_directory: e.target.value })}
                    placeholder="/opt/norscom/node-instances"
                  />
                </Form.Group>

                <Form.Group controlId="formReconnectInterval" className="mb-3">
                  <Form.Label>Reconnect Interval (seconds)</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={formValues.reconnect_interval ?? 5}
                    onChange={(e) => setFormValues({ ...formValues, reconnect_interval: parseInt(e.target.value, 10) || 5 })}
                  />
                </Form.Group>

                <Form.Check
                  type="switch"
                  id="formSkipSslValidation"
                  label="Skip SSL Validation"
                  checked={formValues.skip_ssl_validation ?? false}
                  onChange={(e) => setFormValues({ ...formValues, skip_ssl_validation: e.target.checked })}
                  className="mb-3"
                />

                <Form.Check
                  type="switch"
                  id="formAcceptSelfSigned"
                  label="Accept Self-Signed Certificates"
                  checked={formValues.accept_self_signed ?? false}
                  onChange={(e) => setFormValues({ ...formValues, accept_self_signed: e.target.checked })}
                  className="mb-3"
                />

                <Form.Group controlId="formSapBinaryDirectory" className="mb-3">
                  <Form.Label>SAP Binary Directory</Form.Label>
                  <Form.Control
                    type="text"
                    value={formValues.sap_binary_directory || ''}
                    onChange={(e) => setFormValues({ ...formValues, sap_binary_directory: e.target.value })}
                    placeholder="Optional"
                  />
                </Form.Group>

                <Form.Group controlId="formSapDyldLibraryPath" className="mb-3">
                  <Form.Label>SAP DYLD Library Path</Form.Label>
                  <Form.Control
                    type="text"
                    value={formValues.sap_dyld_library_path || ''}
                    onChange={(e) => setFormValues({ ...formValues, sap_dyld_library_path: e.target.value })}
                    placeholder="Optional"
                  />
                </Form.Group>
              </Form>
            </Tab>

            <Tab eventKey="status" title="Status History">
              {selectedWorker?.status_history?.length ? (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedWorker.status_history.map((entry: any, index: number) => (
                        <tr key={index}>
                          <td>
                            <FormatDateTime value={entry.timestamp} />
                          </td>
                          <td>{entry.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted">No status history available.</p>
              )}
            </Tab>

            <Tab eventKey="logging" title="Logging">
              {!formValues.worker_id ? (
                <p className="text-muted">Save the worker first to view and download logs.</p>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Agent Logs</h6>
                    <Button
                      variant="phoenix-secondary"
                      size="sm"
                      onClick={() => {
                        if (formValues.worker_id !== undefined) loadAgentLogs(formValues.worker_id);
                      }}
                      disabled={isLoadingAgentLogs}
                    >
                      {isLoadingAgentLogs ? 'Loading...' : 'Refresh'}
                    </Button>
                  </div>

                  {agentLogsError && <p className="text-danger mb-3">{agentLogsError}</p>}
                  {agentLogsInfo && <p className="text-muted mb-3">{agentLogsInfo}</p>}

                  {isLoadingAgentLogs ? (
                    <div className="d-flex align-items-center gap-2">
                      <Spinner animation="border" size="sm" />
                      <span>Loading logs...</span>
                    </div>
                  ) : agentLogFiles.length === 0 ? (
                    <p className="text-muted mb-0">No log files available.</p>
                  ) : (
                    <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>File Name</th>
                            <th>Size</th>
                            <th>Modified</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentLogFiles.map((file) => (
                            <tr key={file.file_name}>
                              <td>{file.file_name}</td>
                              <td>{formatFileSize(file.size_bytes)}</td>
                              <td>{file.modified_at ? <FormatDateTime value={file.modified_at} /> : 'N/A'}</td>
                              <td>
                                <Button
                                  variant="phoenix-primary"
                                  size="sm"
                                  onClick={() => {
                                    if (formValues.worker_id !== undefined) handleDownloadAgentLog(formValues.worker_id, file.file_name);
                                  }}
                                  disabled={downloadingLogFileName === file.file_name}
                                >
                                  {downloadingLogFileName === file.file_name ? 'Downloading...' : 'Download'}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}
            </Tab>

            <Tab eventKey="install_script" title="Install Script">
              {!formValues.worker_id ? (
                <p className="text-muted">Save the worker first to generate an install script.</p>
              ) : (
                <Form>
                  <p className="text-muted mb-3">
                    Generate a full install script with embedded config.yaml for this worker. The agent configuration fields from the Worker tab will be
                    included.
                  </p>

                  <Form.Group controlId="formInstallDownloadUrl" className="mb-3">
                    <Form.Label>Binary Download URL</Form.Label>
                    <Form.Control
                      type="text"
                      value={installScriptDownloadUrl}
                      onChange={(e) => setInstallScriptDownloadUrl(e.target.value)}
                      placeholder="https://napdownloads.file.core.windows.net/..."
                    />
                    <Form.Text className="text-muted">
                      Optional. If provided, the script will download the agent binary automatically. Otherwise the binary must be present in the working
                      directory.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group controlId="formInstallServerUrl" className="mb-3">
                    <Form.Label>Server URL (WebSocket)</Form.Label>
                    <Form.Control
                      type="text"
                      value={installScriptServerUrl}
                      onChange={(e) => setInstallScriptServerUrl(e.target.value)}
                      placeholder="wss://ws.example.norscom.com"
                    />
                  </Form.Group>

                  <Form.Group controlId="formInstallWorkerSecret" className="mb-3">
                    <Form.Label>Worker Secret</Form.Label>
                    <Form.Control
                      type="password"
                      value={installScriptSecret}
                      onChange={(e) => setInstallScriptSecret(e.target.value)}
                      placeholder="Enter the worker secret (plaintext)"
                    />
                    <Form.Text className="text-muted">Required for config.yaml generation. The secret is not stored.</Form.Text>
                  </Form.Group>

                  {installScriptError && <p className="text-danger mb-3">{installScriptError}</p>}

                  <Button variant="phoenix-primary" onClick={handleGenerateInstallScript} disabled={isGeneratingScript}>
                    {isGeneratingScript ? 'Generating...' : 'Generate & Download Install Script'}
                  </Button>
                </Form>
              )}
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleRequestClose}>
            Close
          </Button>
          <Button variant="phoenix-primary" onClick={handleSaveWorker} disabled={isSubmitting}>
            {formValues.worker_id ? 'Save and Close' : 'Create Worker'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          resetAndClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </>
  );
};

export default EditWorkerModal;
